import React, { useEffect, useState, useMemo } from "react";
import "./HomePage.css";
import { listenToAcceptedRequests, listenToWaitingRequests } from "../services/simpleFirebase";
import { listenToHabitualCarpools } from "../services/habitualCarpoolsService";
import { getNextMeetings, getNextMeetingsAsync, onMeetingsCacheUpdate, formatMeetingLabel, toIsoDate } from "../utils/dateHelpers";
import Chat from "./Chat";
import IdentitySelector from "./IdentitySelector";
import { getChatId, hasMessages, markChatAsReadLocal } from "../services/chatService";

const HomePage = ({ onNavigate }) => {
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [waitingRequests, setWaitingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openChat, setOpenChat] = useState(null); // { requestId, driverName, passengerName }
  const [currentUserName, setCurrentUserName] = useState(null);
  const [showIdentitySelector, setShowIdentitySelector] = useState(null); // { requestId, driverName, passengerName }
  const [chatsWithMessages, setChatsWithMessages] = useState(new Set()); // Set: chatIds qui ont des messages
  const [habitualCarpools, setHabitualCarpools] = useState([]);
  const [showHabitualCarpools, setShowHabitualCarpools] = useState(false);

  useEffect(() => {
    const unsubscribe = listenToAcceptedRequests((requests) => {
      setAcceptedRequests(requests);
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Écouter les demandes non pourvues (non assignées) en temps réel
  useEffect(() => {
    const unsubscribe = listenToWaitingRequests((requests) => {
      setWaitingRequests(requests || []);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Charger le nom de l'utilisateur actuel depuis localStorage
  useEffect(() => {
    const storedName = localStorage.getItem("cvtrg_current_user");
    if (storedName) {
      setCurrentUserName(storedName);
    }
  }, []);

  // Charger les covoiturages habituels (configurés via admin)
  useEffect(() => {
    const unsubscribe = listenToHabitualCarpools((items) => {
      setHabitualCarpools(items || []);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Gérer l'ouverture du chat
  const handleOpenChat = (requestId, driverName, passengerName, userRole) => {
    // Toujours afficher le sélecteur d'identité pour permettre de choisir qui on est
    setShowIdentitySelector({ requestId, driverName, passengerName });
  };

  // Marquer un chat comme lu quand on le ferme
  const handleChatOpened = (requestId, driverName, passengerName) => {
    const chatId = getChatId(requestId, driverName, passengerName);
    markChatAsReadLocal(chatId);
  };

  // Gérer la sélection d'identité
  const handleIdentitySelect = (selectedName, chatInfo) => {
    setCurrentUserName(selectedName);
    localStorage.setItem("cvtrg_current_user", selectedName);
    setShowIdentitySelector(null);
    // Ouvrir le chat avec l'identité sélectionnée
    if (chatInfo) {
      setOpenChat(chatInfo);
    }
  };

  // Trouver la prochaine réunion avec mise à jour réactive
  const [nextMeeting, setNextMeeting] = useState(() => {
    const meetings = getNextMeetings(1);
    return meetings.length > 0 ? meetings[0] : null;
  });

  useEffect(() => {
    const updateNextMeeting = async () => {
      try {
        const meetings = await getNextMeetingsAsync(1);
        setNextMeeting(meetings.length > 0 ? meetings[0] : null);
      } catch (error) {
        console.warn('⚠️ [HomePage] Erreur chargement async meetings:', error);
      }
    };

    updateNextMeeting();

    // Écouter les mises à jour du cache réactif
    const unsubscribe = onMeetingsCacheUpdate(async () => {
      console.log('🔄 [HomePage] Cache mis à jour, rechargement...');
      await updateNextMeeting();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Filtrer les demandes acceptées pour la prochaine réunion
  const nextMeetingAcceptedRequests = useMemo(() => {
    if (!nextMeeting || acceptedRequests.length === 0) return [];

    const nextIsoDate = nextMeeting.isoDate;
    const now = Date.now();

    return acceptedRequests
      .filter((request) => {
        // Filtrer par date ISO
        const requestIsoDate = request.isoDate || (request.meetingAt ? toIsoDate(request.meetingAt) : null);
        if (requestIsoDate !== nextIsoDate) return false;

        // S'assurer que la réunion est dans le futur
        const meetingAt = request.meetingAt || nextMeeting.meetingAt;
        return meetingAt >= now;
      })
      .sort((a, b) => {
        // Trier par nom de passager
        const nameA = (a.name || a.passengerName || "").toLowerCase();
        const nameB = (b.name || b.passengerName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [acceptedRequests, nextMeeting]);

  // Badge "veille de réunion" sur le bouton liste d'attente
  const waitingBadgeCount = useMemo(() => {
    if (!nextMeeting) return 0;
    const meetingAt = Number(nextMeeting.meetingAt || 0);
    if (!Number.isFinite(meetingAt) || meetingAt <= 0) return 0;

    // Règle simple: avant la prochaine réunion, afficher l'indice si demandes non pourvues
    if (Date.now() >= meetingAt) return 0;

    const meetingIso = nextMeeting.isoDate || toIsoDate(meetingAt);
    const nextIsoDate = meetingIso;
    const matching = (waitingRequests || []).filter((r) => {
      const requestIso = r?.isoDate || (r?.meetingAt ? toIsoDate(r.meetingAt) : null);
      return requestIso === nextIsoDate;
    });

    return matching.length;
  }, [nextMeeting, waitingRequests]);

  // Vérifier s'il y a des messages pour chaque conversation
  useEffect(() => {
    if (nextMeetingAcceptedRequests.length === 0) {
      setChatsWithMessages(new Set());
      return;
    }

    const unsubscribes = [];

    nextMeetingAcceptedRequests.forEach((request) => {
      const drivers = request.drivers ? Object.keys(request.drivers) : (request.assignedTo ? [request.assignedTo] : []);
      const passengerName = request.name || request.passengerName || "Nom non renseigné";

      drivers.forEach((driverKey) => {
        // Récupérer le nom réel du conducteur depuis les données
        const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
        const driverData = request.drivers ? (request.drivers[driverKey] || request.drivers[normalizeKey(driverKey)]) : null;
        const driverName = driverData?.driverName || driverKey; // Utiliser le nom original si disponible
        const chatId = getChatId(request.id, driverName, passengerName);
        
        const unsubscribe = hasMessages(chatId, (hasMsgs) => {
          setChatsWithMessages(prev => {
            const updated = new Set(prev);
            if (hasMsgs) {
              updated.add(chatId);
            } else {
              updated.delete(chatId);
            }
            return updated;
          });
        });

        unsubscribes.push(unsubscribe);
      });
    });

    return () => {
      unsubscribes.forEach(unsub => unsub && unsub());
    };
  }, [nextMeetingAcceptedRequests]);

  return (
    <div className="home-page">
      <div className="welcome-section">
        <h2>Bienvenue sur le système de covoiturage</h2>
        <p>Choisissez votre rôle pour commencer :</p>
      </div>

      <div className="button-container">
        <button
          className="main-button passenger-button"
          onClick={() => onNavigate("passenger")}
        >
          <span className="button-icon">🚶</span>
          <span className="button-text">
            <strong>J'ai besoin d'un transport</strong>
            <small>Je cherche une place dans une voiture</small>
          </span>
        </button>

        <button
          className="main-button driver-button"
          onClick={() => onNavigate("driver")}
        >
          <span className="button-icon">🚗</span>
          <span className="button-text">
            <strong>J'ai une voiture et je peux aider</strong>
            <small>Je propose des places dans ma voiture</small>
          </span>
        </button>

        <button
          className="main-button"
          onClick={() => onNavigate("waiting-list")}
        >
          <span className="button-icon">📝</span>
          <span className="button-text">
            <strong>
              Voir la liste d'attente{" "}
              {waitingBadgeCount > 0 && (
                <span className="waiting-badge" aria-label={`${waitingBadgeCount} demande(s) non pourvue(s)`}>
                  {waitingBadgeCount}
                </span>
              )}
            </strong>
            <small>Consulter les demandes en cours</small>
          </span>
        </button>
      </div>

      <div className="info-section">
        {loading ? (
          <p className="info-text">Chargement...</p>
        ) : nextMeetingAcceptedRequests.length > 0 ? (
          <div className="next-meeting-accepted">
            <p className="next-meeting-title">
              ✅ <strong>Prochaine réunion : {formatMeetingLabel(new Date(nextMeeting.meetingAt))}</strong>
            </p>
            <div className="accepted-table-wrapper">
              <div className="accepted-table">
                <div className="accepted-table-header">
                  <div className="accepted-table-col-chat"></div>
                  <div className="accepted-table-col-passenger">Passager</div>
                  <div className="accepted-table-col-driver">Conducteur</div>
                </div>
                <div className="accepted-table-body">
                {nextMeetingAcceptedRequests.map((request) => {
                  // Récupérer tous les conducteurs depuis request.drivers
                  const drivers = request.drivers ? Object.keys(request.drivers) : (request.assignedTo ? [request.assignedTo] : []);
                  
                  const passengerName = request.name || request.passengerName || "Nom non renseigné";
                  
                  // Si plusieurs conducteurs, créer une ligne par conducteur
                  if (drivers.length > 0) {
                    // Calculer l'état global des places pour cette demande
                    const totalSeatsNeeded = Number(request.seatsNeeded || 1);
                    const seatsAssigned = Number(request.seatsAssigned || 0);
                    const seatsRemaining = Number(request.seatsRemaining !== undefined ? request.seatsRemaining : (totalSeatsNeeded - seatsAssigned));
                    const isFullyAssigned = seatsRemaining === 0 && seatsAssigned >= totalSeatsNeeded;
                    
                    return drivers.map((driverName) => {
                      // Normaliser la clé pour accéder aux données (compatibilité avec les noms contenant des caractères spéciaux)
                      const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                      const driverKey = normalizeKey(driverName);
                      const driverData = request.drivers ? (request.drivers[driverKey] || request.drivers[driverName]) : null; // Support ancien format
                      const actualDriverName = driverData?.driverName || driverName; // Utiliser le nom original si disponible
                      
                      // Récupérer l'icône du conducteur
                      const driverIcon = driverData?.icon || '🚗';
                      
                      // Récupérer le nombre de places prises par ce conducteur
                      const seatsTakenByDriver = driverData ? (driverData.seatsTaken || 1) : 1;
                      
                      // Construire le texte d'affichage des places
                      let displayText = '';
                      if (totalSeatsNeeded > 1) {
                        if (isFullyAssigned) {
                          // Toutes les places sont prises - afficher "Complet" ou le ratio final
                          displayText = ` (${totalSeatsNeeded}/${totalSeatsNeeded} - Complet)`;
                        } else {
                          // Afficher le ratio actuel
                          displayText = ` (${seatsAssigned}/${totalSeatsNeeded})`;
                        }
                      }
                      
                      // Utiliser le nom original pour le chat (mais garder driverName pour la clé)
                      const chatId = getChatId(request.id, actualDriverName, passengerName);
                      const hasMsgs = chatsWithMessages.has(chatId);

                      return (
                        <div key={`${request.id}_${driverName}`} className="accepted-table-row">
                          <div className="accepted-table-col-chat">
                            <button
                              className={`chat-button ${hasMsgs ? 'chat-button-has-messages' : ''}`}
                              onClick={() => {
                                handleOpenChat(request.id, actualDriverName, passengerName, 'chat');
                              }}
                              title={hasMsgs ? "Ouvrir le chat (messages disponibles)" : "Ouvrir le chat"}
                            >
                              💬
                            </button>
                          </div>
                          <div className="accepted-table-col-passenger">
                            👤 {passengerName}
                          </div>
                          <div className="accepted-table-col-driver">
                            <span className={`driver-badge ${isFullyAssigned ? 'driver-badge-complete' : ''}`}>
                              <span className="driver-icon">{driverIcon}</span>
                              <span className="driver-name">{actualDriverName}{displayText}</span>
                            </span>
                          </div>
                        </div>
                      );
                    });
                  } else {
                    // Pas de conducteur assigné
                    return (
                      <div key={request.id} className="accepted-table-row">
                        <div className="accepted-table-col-chat">
                          {/* Pas de bouton de chat si pas de conducteur */}
                        </div>
                        <div className="accepted-table-col-passenger">
                          👤 {passengerName}
                        </div>
                        <div className="accepted-table-col-driver">
                          <span className="no-driver">Non assigné</span>
                        </div>
                      </div>
                    );
                  }
                })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="info-text">
            ✅ <strong>Prochaine réunion : {nextMeeting ? formatMeetingLabel(new Date(nextMeeting.meetingAt)) : "Aucune"}</strong>
            <br />
            Aucune demande acceptée pour le moment.
          </p>
        )}
      </div>

      <div className="habitual-section">
        <button
          type="button"
          className="habitual-toggle"
          onClick={() => setShowHabitualCarpools((v) => !v)}
          aria-expanded={showHabitualCarpools}
        >
          🔁 <strong>Voir les covoiturages habituels</strong>
          <span className="habitual-toggle-chevron">{showHabitualCarpools ? "▲" : "▼"}</span>
        </button>

        {showHabitualCarpools && (
          <div className="habitual-panel">
            {habitualCarpools.length === 0 ? (
              <div className="habitual-empty">
                Aucun covoiturage habituel enregistré.
              </div>
            ) : (
              <div className="habitual-list">
                {habitualCarpools
                  .slice()
                  .sort((a, b) => {
                    const ad = String(a.driverName || "").toLowerCase();
                    const bd = String(b.driverName || "").toLowerCase();
                    const ap = String(a.passengerName || "").toLowerCase();
                    const bp = String(b.passengerName || "").toLowerCase();
                    return ad.localeCompare(bd) || ap.localeCompare(bp);
                  })
                  .map((item) => (
                    <div key={item.id} className="habitual-row">
                      <span className="habitual-driver">{item.driverIcon || "🚗"} {item.driverName}</span>
                      <span className="habitual-arrow">→</span>
                      <span className="habitual-passenger">{item.passengerIcon || "👤"} {item.passengerName}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sélecteur d'identité */}
      {showIdentitySelector && (
        <IdentitySelector
          driverName={showIdentitySelector.driverName}
          passengerName={showIdentitySelector.passengerName}
          onSelect={(selectedName) => handleIdentitySelect(selectedName, showIdentitySelector)}
          onClose={() => setShowIdentitySelector(null)}
        />
      )}

      {/* Composant Chat */}
      {openChat && currentUserName && (() => {
        // Trouver la demande correspondante pour récupérer la description
        const request = nextMeetingAcceptedRequests.find(r => r.id === openChat.requestId);
        return (
          <Chat
            isOpen={true}
            onClose={() => {
              // Marquer comme lu seulement quand on ferme le chat (après avoir vu les messages)
              handleChatOpened(openChat.requestId, openChat.driverName, openChat.passengerName);
              setOpenChat(null);
            }}
            requestId={openChat.requestId}
            driverName={openChat.driverName}
            passengerName={openChat.passengerName}
            currentUserName={currentUserName}
            description={request?.description}
          />
        );
      })()}
    </div>
  );
};

export default HomePage;
